"use client";

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { SectionEventsItemSchema } from "~components/cms/section-events-item/types";

export type EventFilterKeys = "attendance" | "eventTypes" | "countries";

interface FilterEventsContextValue {
  events: SectionEventsItemSchema[];
  filteredEvents: SectionEventsItemSchema[];
  searchQuery: string;
  handleSearchEvents: (value: string) => void;
  clearSearchEvents: () => void;

  // FILTERS
  uniqueAttendance: string[];
  uniqueEventTypes: string[];
  uniqueCountries: string[];

  selectedFilters: {
    attendance: Set<string>;
    eventTypes: Set<string>;
    countries: Set<string>;
  };

  toggleFilter: (filterKey: EventFilterKeys, values: string[]) => void;

  // CLEAR ALL FILTERS
  clearAllFilters: () => void;
  clearFilterByType: (filterKey: EventFilterKeys) => void;
}

const FilterEventsContext = createContext<FilterEventsContextValue | undefined>(
  undefined,
);

interface FilterEventsProviderProps {
  events: SectionEventsItemSchema[];
  children: ReactNode;
}

export function FilterEventsProvider({
  events,
  children,
}: Readonly<FilterEventsProviderProps>) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({
    attendance: new Set<string>(),
    eventTypes: new Set<string>(),
    countries: new Set<string>(),
  });

  const handleSearchEvents = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const clearSearchEvents = useCallback(() => {
    setSearchQuery("");
  }, []);

  const toggleFilter = useCallback(
    (filterKey: EventFilterKeys, values: string[]) => {
      setSelectedFilters(prevFilters => {
        const newFilters = { ...prevFilters };
        newFilters[filterKey] = new Set(values);
        return newFilters;
      });
    },
    [],
  );

  const { uniqueAttendance, uniqueEventTypes, uniqueCountries } =
    useMemo(() => {
      const attendanceSet = new Set<string>();
      const eventTypesSet = new Set<string>();
      const countriesSet = new Set<string>();

      for (const event of events) {
        if (event.attendance) {
          attendanceSet.add(event.attendance);
        }

        if (event.eventType) {
          for (const type of event.eventType) {
            if (type) {
              eventTypesSet.add(type);
            }
          }
        }

        if (event.country) {
          countriesSet.add(event.country);
        }
      }

      return {
        uniqueAttendance: Array.from(attendanceSet).sort(),
        uniqueEventTypes: Array.from(eventTypesSet).sort(),
        uniqueCountries: Array.from(countriesSet).sort(),
      };
    }, [events]);

  const filteredEvents = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const filterKeys = Object.keys(selectedFilters) as Array<
      keyof typeof selectedFilters
    >;

    let filtered = events.filter(event => {
      return filterKeys.every(filterKey => {
        const selectedValues = selectedFilters[filterKey];

        if (selectedValues.size === 0) {
          return true;
        }

        if (filterKey === "eventTypes") {
          return event.eventType?.some(type => selectedValues.has(type));
        }

        if (filterKey === "countries") {
          return event.country && selectedValues.has(event.country);
        }

        if (filterKey === "attendance") {
          return event.attendance && selectedValues.has(event.attendance);
        }

        return true;
      });
    });

    if (searchQuery) {
      filtered = filtered.filter(event => {
        const title = event.text?.[0]?.title?.toLowerCase();
        if (title?.includes(query)) return true;

        const country = event.country?.toLowerCase();
        if (country?.includes(query)) return true;

        const attendance = event.attendance?.toLowerCase();
        if (attendance?.includes(query)) return true;

        const matchesEventType = event.eventType?.some(type =>
          type.toLowerCase().includes(query),
        );
        if (matchesEventType) return true;

        return false;
      });
    }

    return filtered;
  }, [events, searchQuery, selectedFilters]);

  // CLEAR FILTERS
  const clearAllFilters = useCallback(() => {
    setSelectedFilters({
      attendance: new Set<string>(),
      eventTypes: new Set<string>(),
      countries: new Set<string>(),
    });
  }, []);

  const clearFilterByType = useCallback((filterKey: EventFilterKeys) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterKey]: new Set<string>(),
    }));
  }, []);

  return (
    <FilterEventsContext.Provider
      value={{
        events,
        filteredEvents,
        searchQuery,
        handleSearchEvents,
        clearSearchEvents,
        uniqueAttendance,
        uniqueEventTypes,
        uniqueCountries,
        selectedFilters,
        toggleFilter,
        clearAllFilters,
        clearFilterByType,
      }}
    >
      {children}
    </FilterEventsContext.Provider>
  );
}

export function useFilterEvents() {
  const context = useContext(FilterEventsContext);
  if (!context) {
    throw new Error(
      "useFilterEvents must be used within a FilterEventsProvider",
    );
  }
  return context;
}
